import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { tap } from 'rxjs/operators';
import { badgeActions } from '@fitup-monorepo/badges/lib/state/badge.actions';
import { dailyTasksActions } from '@fitup-monorepo/home-dashboard/lib/components/daily-tasks-overview/state/daily-tasks.actions';
import { challengeActions } from '@fitup-monorepo/challenge/lib/state/challenge.actions';
import { profileApiActions } from '@fitup-monorepo/core/lib/state/profile/profile.actions';
import { ActionError } from '@fitup-monorepo/core/lib/model/common-types';
import { isBackendUrl } from '@fitup-monorepo/core/lib/interceptors/backend-request.util';
import { ToastService } from '@fitup-monorepo/core/lib/services/toast/toast.service';
import { mediaCenterActions } from '@fitup-monorepo/media-center/lib/state/mediacenter.actions';
import { clubActions } from '@fitup-monorepo/challenge/lib/state/club/club.actions';
import { Action } from '@ngrx/store';
import { eventsActions } from '@fitup-monorepo/team-hub/lib/pages/events/state/events.actions';

@Injectable()
export class ErrorEffects {
  private readonly actions$ = inject(Actions);
  private readonly toastService = inject(ToastService);

  public handleToastError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          profileApiActions.updateCustomerFailure,
          profileApiActions.setAvatarFailure,
          profileApiActions.deleteImageFailure,
          profileApiActions.uploadImageFailure,
          //profileApiActions.loadProfileFailure,
          mediaCenterActions.updateFavoriteFailure,
          mediaCenterActions.updateWatchProgressFailure,
          clubActions.createClubFailure,
          clubActions.updateClubFailure,
          clubActions.deleteClubFailure,
          eventsActions.loadEventSeriesFailure,
          eventsActions.cancelEventSeriesFailure
        ),
        tap(action => {
          this.logError(action);
          this.toastService.showError(action.errorMessage ?? 'GENERIC_ERROR');
        })
      ),
    { dispatch: false }
  );

  public handleLogging$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          mediaCenterActions.loadMediaEntryFailure,
          mediaCenterActions.loadOverviewFailure,
          mediaCenterActions.loadCategoryFailure,
          mediaCenterActions.loadFavoritesFailure,
          challengeActions.loadCommunityOverviewFailure,
          challengeActions.loadPartnerOverviewFailure,
          challengeActions.markCompletedChallengesShownFailure,
          badgeActions.loadEarnedBadgesFailure,
          dailyTasksActions.loadDailyTasksFailure
        ),
        tap(action => {
          this.logError(action);
        })
      ),
    { dispatch: false }
  );

  private logError(action: ActionError & Action<string>): void {
    console.error(`${action.type}: ${describeActionError(action.error)}`);
  }
}

/**
 * `JSON.stringify` on an `HttpErrorResponse` dumps its (empty) headers object, repeats the url and adds the meaningless
 * `ProgressEvent` of a network failure. The HttpLoggingInterceptor already logged the url and the response details of
 * failed backend requests, so the status is enough to connect the action to that log line.
 */
function describeActionError(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    return error.url && !isBackendUrl(error.url) ? `HTTP ${error.status} ${error.url}` : `HTTP ${error.status}`;
  }
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }
  return JSON.stringify(error);
}
